import { forwardRef, type HTMLAttributes, type ForwardedRef, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

const statusBadgeVariants = cva(
  'font-mono inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-[10px] font-semibold uppercase tracking-[0.12em]',
  {
    variants: {
      tone: {
        idle: 'bg-muted border-border text-muted-foreground',
        live: 'bg-success-tint border-success-line text-success-text',
        busy: 'bg-warning-tint border-warning-line text-warning-text',
        error: 'bg-destructive-tint border-destructive-line text-destructive-text',
        info: 'bg-info-tint border-info-line text-info-text',
      },
    },
    defaultVariants: { tone: 'idle' },
  },
);

// brand pulse class abstracted to the neutral pulse-dot (defined in themes/base.css)
const dotVariants = cva('h-1.5 w-1.5 rounded-full', {
  variants: {
    tone: {
      idle: 'bg-muted-foreground',
      live: 'bg-success',
      busy: 'bg-warning',
      error: 'bg-destructive',
      info: 'bg-info',
    },
    pulse: { true: 'pulse-dot', false: '' },
  },
  defaultVariants: { tone: 'idle', pulse: false },
});

export interface StatusBadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusBadgeVariants> {
  pulse?: boolean;
  children?: ReactNode;
}

export const StatusBadge = forwardRef(function StatusBadge(
  { className, tone = 'idle', pulse = false, children, ...props }: StatusBadgeProps,
  ref: ForwardedRef<HTMLSpanElement>,
) {
  return (
    <span ref={ref} className={cn(statusBadgeVariants({ tone }), className)} {...props}>
      <span className={dotVariants({ tone, pulse })} />
      {children}
    </span>
  );
});
